/**
 * **Dazwischen anlegen** — wohin ein neuer Zettel kommt, der über einen
 * **Anlegepunkt** entsteht, und wer ihm dafür Platz macht.
 *
 * Der Zettel drängt sich in eine **Reihe** hinein, statt an ihr vorbeizurücken:
 * Die Reihe rückt, und zwar immer nach unten oder rechts, nie nach oben oder
 * links (CONTEXT.md, „Dazwischen anlegen"). Am unteren und rechten Punkt bleibt
 * der ausgewählte Zettel stehen; am oberen und linken nimmt der neue seinen
 * Platz ein, und er rückt als erstes Glied der Reihe selbst mit.
 *
 * **Ist an der Kante frei, rückt niemand** — dann heißt der Punkt schlicht
 * „hier einen anlegen". Gilt für alle vier Kanten.
 *
 * Bewusst ohne React-Bezug, wie `canvasDuplicate.ts`: Die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein, und die Vorschau beim Überfahren muss
 * dieselbe Lage zeigen, die der Klick dann herstellt.
 */

import type { MovingRect } from './canvasAlignment'
import { DUPLICATE_GAP } from './canvasDuplicate'

export type SpawnSide = 'top' | 'right' | 'bottom' | 'left'

export const SPAWN_SIDES: readonly SpawnSide[] = ['top', 'right', 'bottom', 'left']

/**
 * Abstand zwischen Original und neuem Zettel, wenn die Reihe keinen eigenen
 * **Takt** vorgibt — derselbe wie beim Duplizieren, und damit auch derselbe,
 * auf den das Einrasten neben eine fremde Kante rastet.
 */
export const SPAWN_GAP = DUPLICATE_GAP

/**
 * Wie lange das Anlegen dauert — der neue Zettel wächst auf, die verdrängten
 * gleiten an ihren Platz.
 *
 * EINE Zahl für beides: Es ist eine Bewegung, keine zwei. Sie gilt auch fürs
 * Zurücknehmen (Zeiger runter vom Punkt) und steckt als
 * `--canvas-spawn-anim` am Canvas, damit `CanvasFlow.scss` sie nicht ein
 * zweites Mal aufschreiben muss.
 */
export const SPAWN_ANIM_MS = 240

/** Ein Canvas-Element, wie es in die Rechnung geht. */
export interface InsertEntry {
  id: string
  rect: MovingRect
  /**
   * Der **Bereich**, zu dem es gehört — `null`/fehlend heißt: keiner, es liegt
   * im Freien. Die Reihe endet an dieser Grenze (siehe `rowAfter`).
   */
  areaId?: string | null
  /**
   * Ob ein **Klebestreifen** darauf liegt. Ein geklebter Zettel ist eine
   * **Wand** wie die Bereichskante: Er rückt nie, und hinter ihm rückt auch
   * niemand mehr (siehe `rowAfter`).
   */
  taped?: boolean
}

/**
 * Die **Welt** eines Elements: sein **Bereich**, oder `null` fürs Freie.
 *
 * Fehlt die Angabe, gilt „frei" — ein Zugeständnis an Tests und Geschichten;
 * in der App setzt `CanvasFlow` sie immer.
 */
export const worldOf = (entry: { areaId?: string | null }): string | null => entry.areaId ?? null

/** Wohin ein Element rückt, damit der neue Zettel Platz hat. */
export interface InsertMove {
  id: string
  x: number
  y: number
}

export interface InsertOptions {
  /**
   * **Rücken, auch wenn an der Kante Platz wäre.** Für das **Dazwischen
   * schieben**: Dort nimmt der Getragene den Platz ein, auf dessen Kante man
   * zeigt, und was dahinter liegt, rückt — sonst wirkte dieselbe Geste am
   * ERSTEN einer Reihe anders als in ihrer Mitte (der Getragene legte sich
   * darüber, statt den Platz zu nehmen).
   *
   * Für das **Dazwischen anlegen** bleibt es aus: Das Plus über einem
   * freistehenden Zettel heißt „leg hier oben einen an", nicht „schieb dich
   * selbst nach unten".
   */
  push?: boolean
}

export interface InsertPlan {
  /** Das Rechteck des neuen Zettels, in Canvas-Koordinaten. */
  rect: MovingRect
  /** Wer wohin rückt. Wer stehen bleibt, steht hier nicht. */
  moves: InsertMove[]
  /**
   * **Kein Raum**: An der Kante ist weder Platz, noch darf jemand rücken —
   * hinter ihr steht eine **Wand** (ein Zettel aus einer anderen Welt, siehe
   * `rowAfter`). `rect` sagt dann nur, wo der Zettel LÄGE; niemand darf ihn
   * dorthin legen. Der **Anlegepunkt** wird davon still (`spawnTargetFor`),
   * und einen **Rand** gibt es dort nicht (`pushes` in `canvasSwapZones`).
   */
  blocked: boolean
}

/** Die Werte eines Rechtecks auf der Achse, entlang der gerückt wird. */
export interface AxisView {
  /** Führende Kante — links bzw. oben. */
  lead: number
  /** Folgende Kante — rechts bzw. unten. */
  trail: number
  /** Ausdehnung auf der ANDEREN Achse — dort muss überlappt werden. */
  crossStart: number
  crossEnd: number
}

export const viewOf = (rect: MovingRect, horizontal: boolean): AxisView =>
  horizontal
    ? { lead: rect.x, trail: rect.x + rect.width, crossStart: rect.y, crossEnd: rect.y + rect.height }
    : { lead: rect.y, trail: rect.y + rect.height, crossStart: rect.x, crossEnd: rect.x + rect.width }

/** Was von einer **Reihe** herauskommt (siehe `rowAfter`). */
export interface Row {
  /** Die Glieder, die rücken — alle aus derselben **Welt** wie der Ursprung. */
  entries: InsertEntry[]
  /** Ob sie an einer **Wand** endet: am nächsten Nachfolger aus einer anderen Welt. */
  wall: boolean
  /** Die führende Kante des ersten Nachfolgers in Reichweite — Wand oder Glied. */
  lead: number | null
}

/**
 * Die **Reihe**: der ausgewählte Zettel und alle, die ihm in Rückrichtung
 * dicht folgen.
 *
 * Dicht heißt zweierlei: Sie überlappen den AUSGEWÄHLTEN quer — für eine
 * senkrechte Reihe also in der Waagerechten —, und die Lücke zum vorigen Glied
 * ist höchstens eine Zettellänge. Die erste größere Lücke beendet die Reihe.
 *
 * Quer gemessen wird gegen den ausgewählten Zettel und nicht gegen das jeweils
 * vorige Glied: Sonst wanderte die Reihe über schräg versetzte Nachbarn davon,
 * und niemand könnte ihr am Bild ansehen, wo sie endet. Nur ECHTE Überlappung
 * zählt (`>`), wie bei den Abstandsklammern (`canvasSpacing.ts`): Bündig
 * aneinanderstoßende Spalten sind Nachbarn, keine Reihe.
 *
 * **Und sie endet an einer Wand** (CONTEXT.md, Bereich und Klebestreifen).
 * Zwei Dinge sind eine: ein Zettel aus einer anderen **Welt** — einem anderen
 * **Bereich** oder dem Freien — und ein **geklebter** Zettel. Beide rücken
 * nie, und hinter beiden rückt auch niemand mehr: Er stünde sonst nach dem
 * Rücken auf ihnen. Die Bereichskante wurde 2026-09-05 mit Bild gemeldet —
 * eine freie Notiz wurde weit weg abgelegt, und die drei Zettel eines Bereichs
 * rückten mit aus ihrer Karte heraus, weil sich der Ursprung quer durch den
 * Bereich schloss.
 *
 * @param world der **Bereich** des Ursprungs (`null` fürs Freie)
 */
export function rowAfter(
  origin: AxisView,
  others: { entry: InsertEntry; view: AxisView }[],
  reach: number,
  world: string | null,
): Row {
  const following = others
    .filter(o => o.view.crossEnd > origin.crossStart && o.view.crossStart < origin.crossEnd)
    .filter(o => o.view.lead > origin.lead)
    .sort((a, b) => a.view.lead - b.view.lead)

  const entries: InsertEntry[] = []
  let previous = origin
  let lead: number | null = null
  let wall = false
  for (const { entry, view } of following) {
    if (view.lead - previous.trail > reach) break
    // Der erste in Reichweite gibt den **Takt** vor — auch eine Wand: Sonst
    // hieße „dahinter ist frei", was in Wahrheit besetzt ist.
    if (lead === null) lead = view.lead
    if (worldOf(entry) !== world || entry.taped) {
      wall = true
      break
    }
    entries.push(entry)
    previous = view
  }
  return { entries, wall, lead }
}

/**
 * Der **Takt** der Reihe — die Lücke, die auch der neue Zettel bekommt.
 *
 * Genommen wird die Lücke zum Nachbarn auf der Seite, an der der Punkt sitzt:
 * unten/rechts der erste in Rückrichtung, oben/links der davor. Steht dort
 * niemand dicht genug, gilt der übliche Abstand — eine Lücke, die weiter ist
 * als eine Zettellänge, ist kein Takt, sondern Leere.
 */
function taktOf(gap: number | null, reach: number): number {
  return gap !== null && gap > 0 && gap <= reach ? gap : SPAWN_GAP
}

/**
 * Wo der neue Zettel liegt und wer dafür rückt.
 *
 * Ist der Platz an der Kante frei, liegt er dort und `moves` bleibt leer.
 *
 * @param origin  der ausgewählte Zettel, an dessen Kante der Punkt sitzt
 * @param side    die geklickte Kante
 * @param size    die Maße des NEUEN Zettels — er rückt die Reihe um seine
 *                eigene Länge plus den Takt, nicht um die des ausgewählten
 * @param others  alle übrigen Elemente; der ausgewählte darf darunter stehen
 * @param options `push: true` hebt die Frei-Regel auf — siehe `InsertOptions`
 */
export function getInsertPlan(
  origin: InsertEntry,
  side: SpawnSide,
  size: { width: number; height: number },
  others: readonly InsertEntry[],
  options: InsertOptions = {},
): InsertPlan {
  const horizontal = side === 'left' || side === 'right'
  /* Der ausgewählte gibt die Zettellänge vor, an der die Reihe endet. */
  const reach = horizontal ? origin.rect.width : origin.rect.height
  const view = viewOf(origin.rect, horizontal)
  const rest = others
    .filter(o => o.id !== origin.id)
    .map(entry => ({ entry, view: viewOf(entry.rect, horizontal) }))

  const { entries: row, wall, lead: nextLead } = rowAfter(view, rest, reach, worldOf(origin))

  /*
    Der Takt: unten/rechts die Lücke zum ersten Nachfolger, oben/links die
    zum Zettel DAVOR. Der davor ist der nächste quer überlappende Nachbar in
    Gegenrichtung — er rückt selbst nicht mit, gibt aber den Rhythmus vor, in
    den sich der neue Zettel einfügt.

    Gemessen wird gegen den ersten Nachfolger, nicht gegen das erste GLIED:
    Eine **Wand** ist kein Glied, steht aber trotzdem da. Sonst hieße „hinter
    der Kante ist frei", was in Wahrheit besetzt ist.
  */
  const before = side === 'top' || side === 'left'
  let gap: number | null = null
  if (before) {
    const trails = rest
      .filter(o => o.view.crossEnd > view.crossStart && o.view.crossStart < view.crossEnd)
      .filter(o => o.view.trail <= view.lead)
      .map(o => o.view.trail)
    gap = trails.length > 0 ? view.lead - Math.max(...trails) : null
  } else {
    gap = nextLead === null ? null : nextLead - view.trail
  }
  const takt = taktOf(gap, reach)
  const length = horizontal ? size.width : size.height
  const shift = length + takt

  /*
    **Ist an der Kante Platz, rückt niemand.** Das Plus über einem freistehenden
    Zettel heißt „leg hier oben einen an" — nicht „schieb dich selbst nach
    unten". Platz heißt: Die Lücke zum Nachbarn auf DIESER Seite trägt den neuen
    Zettel samt Abstand.

    Unten und rechts galt das schon: Dort ist die Lücke `null`, sobald niemand
    dicht genug folgt, und die Reihe ist dann leer. Diese Zeile schreibt für
    alle vier Kanten auf, was dort galt.

    In diesem Zweig ist der Takt immer `SPAWN_GAP`: Gäbe ein Nachbar einen Takt
    vor (Lücke höchstens eine Zettellänge), passte der neue Zettel per
    Definition nicht mehr hinein — er ist ja länger als nichts.
  */
  const free = !options.push && (gap === null || gap >= length + takt)

  /*
    Quer bleibt der neue Zettel auf der Kante ZENTRIERT — eine Notiz neben
    einer Notiz steht damit mittig zu ihm, wie eh und je.
  */
  const crossLead = horizontal
    ? origin.rect.y + (origin.rect.height - size.height) / 2
    : origin.rect.x + (origin.rect.width - size.width) / 2

  const lead = before
    ? free ? view.lead - takt - length : view.lead
    : view.trail + takt
  const rect: MovingRect = horizontal
    ? { x: lead, y: crossLead, ...size }
    : { x: crossLead, y: lead, ...size }

  /*
    **Kein Raum**: Die Kante ist nicht frei, und die Reihe dahinter endet an
    einer **Wand** — einem Zettel aus einer anderen Welt. Er rückt nicht, also
    kann auch niemand vor ihm rücken; oben/links gilt das für den Ausgewählten
    selbst, der ja das erste Glied wäre. Dann rührt sich nichts, und der Plan
    sagt es: Wer ihn ausführte, legte den neuen Zettel auf einen fremden.
  */
  const blocked = !free && wall

  // Oben und links ist der ausgewählte das erste Glied der Reihe: Der neue
  // nimmt seinen Platz ein, also rückt er selbst mit — aber nur, wenn dieser
  // Platz überhaupt gebraucht wird.
  const moving = free || blocked ? [] : before ? [origin, ...row] : row
  const moves = moving.map(entry => ({
    id: entry.id,
    x: horizontal ? entry.rect.x + shift : entry.rect.x,
    y: horizontal ? entry.rect.y : entry.rect.y + shift,
  }))

  return { rect, moves, blocked }
}

/**
 * Wie nah der Zeiger einer Kante kommen muss, damit ihr Anlegepunkt sich
 * hebt — in BILDSCHIRM-Pixeln, wie `DUPLICATE_DRAG_THRESHOLD`: gegen den
 * Zoom zu rechnen, sonst wäre die Zone herausgezoomt winzig.
 */
export const SPAWN_NEAR_DISTANCE = 36

/**
 * Welcher Kante des Zettels der Zeiger nah ist — oder `null`.
 *
 * Nah heißt: höchstens `SPAWN_NEAR_DISTANCE` (Bildschirm-Pixel) von der
 * Kantenlinie entfernt, innen wie außen, und in ihrer Länge — mit derselben
 * Toleranz über die Ecken hinaus, damit der Punkt auch von schräg außen
 * erreichbar ist. Kommen mehrere Kanten in Frage (kleiner Zettel), gewinnt
 * die nächste.
 */
export function getNearSpawnSide(
  rect: MovingRect,
  point: { x: number; y: number },
  zoom: number,
): SpawnSide | null {
  const reach = SPAWN_NEAR_DISTANCE / zoom
  const withinX = point.x >= rect.x - reach && point.x <= rect.x + rect.width + reach
  const withinY = point.y >= rect.y - reach && point.y <= rect.y + rect.height + reach
  if (!withinX || !withinY) return null

  const distances: [SpawnSide, number][] = [
    ['top', Math.abs(point.y - rect.y)],
    ['bottom', Math.abs(point.y - (rect.y + rect.height))],
    ['left', Math.abs(point.x - rect.x)],
    ['right', Math.abs(point.x - (rect.x + rect.width))],
  ]
  const [side, distance] = distances.reduce((best, cur) => (cur[1] < best[1] ? cur : best))
  return distance <= reach ? side : null
}
